import React, { useEffect, useRef, useState } from 'react';
import type { WorkspaceListItem } from '@deckspace/types';
import { PanelRightOpen, MoreVertical, Cloud } from 'lucide-react';
import type { SyncStateDisplay } from './CollaborationBar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export interface WorkspaceSidebarProps {
  /** Whether the sidebar is visible */
  isOpen: boolean;
  /** Called when sidebar should close (e.g., click outside, toggle button) */
  onClose: () => void;
  /** Array of workspaces to display */
  workspaces: WorkspaceListItem[];
  /** ID of the currently active workspace */
  activeWorkspaceId: string | null;
  /** Called when user clicks on a workspace to switch to it */
  onSelectWorkspace: (id: string) => void;
  /** Called when user requests to delete a workspace */
  onDeleteWorkspace: (id: string) => void;
  /** Called when user requests to rename a workspace */
  onRenameWorkspace: (id: string) => void;
  /** Called when user requests to export a workspace */
  onExportWorkspace: (id: string) => void;
  /** Per-workspace sync states for status dots */
  syncStates?: Map<string, SyncStateDisplay>;
  /** Top offset in pixels (default: 0) */
  topOffset?: number;
}

/**
 * A collapsible sidebar for workspace navigation.
 *
 * Features:
 * - Hidden by default, pops out when toggled
 * - Closes when clicking outside
 * - Shows all workspaces with individual menu actions
 * - Theme-aware styling
 *
 * @example
 * ```tsx
 * const [sidebarOpen, setSidebarOpen] = useState(false);
 *
 * <WorkspaceSidebar
 *   isOpen={sidebarOpen}
 *   onClose={() => setSidebarOpen(false)}
 *   workspaces={workspaces}
 *   activeWorkspaceId={activeWorkspaceId}
 *   onSelectWorkspace={handleSelectWorkspace}
 *   onDeleteWorkspace={handleDeleteWorkspace}
 *   onRenameWorkspace={handleRenameWorkspace}
 *   onExportWorkspace={handleExportWorkspace}
 * />
 * ```
 */
export function WorkspaceSidebar({
  isOpen,
  onClose,
  workspaces,
  activeWorkspaceId,
  onSelectWorkspace,
  onDeleteWorkspace,
  onRenameWorkspace,
  onExportWorkspace,
  syncStates,
  topOffset = 0,
}: WorkspaceSidebarProps) {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Handle open/close animation states
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
      return;
    }

    if (!isVisible) return;

    // Start closing animation
    setIsClosing(true);
    // Remove from DOM after animation completes (300ms = sidebar animation duration)
    const timer = setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen, isVisible]);

  // Close sidebar when clicking outside
  useEffect(() => {
    if (!isVisible || isClosing) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, isClosing, onClose]);

  if (!isVisible) return null;

  const backdropClass = isClosing ? 'animate-fade-out' : 'animate-fade-in';
  const sidebarClass = isClosing ? 'animate-slide-out-left' : 'animate-slide-in-left';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 ${backdropClass}`}
        onClick={onClose}
        style={{ top: topOffset }}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed left-0 bottom-0 w-72 bg-card border-r border-border z-50 overflow-y-auto flex flex-col ${sidebarClass}`}
        style={{ top: topOffset }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">Workspaces</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background rounded-md transition-colors text-foreground"
            title="Close sidebar"
          >
            <PanelRightOpen size={18} />
          </button>
        </div>

        {/* Workspace List */}
        <div className="flex-1 overflow-y-auto">
          {workspaces.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-muted-foreground">
              No workspaces yet
            </div>
          ) : (
            <div className="space-y-1.5 p-4">
              {workspaces.map((workspace) => (
                <div
                  key={workspace.id}
                  onClick={() => {
                    onSelectWorkspace(workspace.id);
                    onClose();
                  }}
                  className={`group relative flex items-center px-4 py-2.5 rounded-md transition-colors cursor-pointer ${
                    workspace.id === activeWorkspaceId
                      ? 'bg-primary/20 text-primary'
                      : 'text-foreground hover:bg-popover'
                  }`}
                >
                  {/* Workspace Name - Left Aligned */}
                  <span
                    className="flex-1 flex items-center gap-1.5 text-sm truncate"
                    title={workspace.name || workspace.id}
                  >
                    {workspace.name || workspace.id}
                    <Cloud
                      size={14}
                      className="text-muted-foreground flex-shrink-0"
                      data-testid="cloud-icon"
                    />
                    {syncStates?.has(workspace.id) &&
                      (() => {
                        const state = syncStates.get(workspace.id)!;
                        const dotColor =
                          state.status === 'connected'
                            ? 'bg-green-500'
                            : state.status === 'connecting'
                              ? 'bg-yellow-500'
                              : 'bg-red-500';
                        return (
                          <span
                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}
                            data-testid={`sync-dot-${workspace.id}`}
                            title={state.status === 'error' ? state.error || 'Error' : state.status}
                          />
                        );
                      })()}
                  </span>

                  {/* Menu Button - Right Aligned */}
                  <div className="ml-3 flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-background rounded-md transition-opacity"
                          title="Workspace options"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onRenameWorkspace(workspace.id)}>
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onExportWorkspace(workspace.id)}>
                          Export
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => onDeleteWorkspace(workspace.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
